import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { getSocket } from '../../../services/socket';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { activeUsersRequest } from '../../../store/lobby/lobbyActions';
import { useDevice } from '../../../utils/hooks/useDevice';
import { playSound } from '../../../utils/sound';
import { RootStackParamList } from '../../navigator';
import HomeDesktopContent from '../HomeRedesign/HomeDesktopContent';
import HomeRedesignContent from '../HomeRedesign/HomeRedesignContent';
import ErrorFindTableModal from '../Modals/ErrorFindTableModal';

type NavigationProp = NativeStackNavigationProp<RootStackParamList, 'Game'>;
type Nav = NativeStackNavigationProp<RootStackParamList>;

const MiddleSection = () => {
	const { navigate } = useNavigation<Nav>();
	const isLogged = useSelector(selectIsLogged);
	const { isDesktop } = useDevice();
	const navigation = useNavigation<NavigationProp>();
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const playerId = useSelector((s: any) => s.auth?.user?.id || '');
	const username = useSelector((s: any) => s.auth?.user?.username || '');
	const [isFindingTable, setIsFindingTable] = useState(false);
	const [showErrorModal, setShowErrorModal] = useState(false);
	const [isInsufficientChipsError, setIsInsufficientChipsError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [errorTitle, setErrorTitle] = useState(t('T_ServerError_31'));
	const timeoutRef = React.useRef<any>(null);
	const requestCleanupRef = React.useRef<(() => void) | null>(null);

	const cleanupActiveRequest = React.useCallback(() => {
		requestCleanupRef.current?.();
		requestCleanupRef.current = null;
	}, []);

	useEffect(() => {
		if (!isLogged || !playerId || !username) return;

		const socket = getSocket(String(playerId), String(username));

		const handleRecovering = (data: any) => {
			console.log('Service recovering...', data);
			setIsFindingTable(false);
		};

		const handleRecovered = () => {
			console.log('Service recovered!');
		};

		socket.on('game:recovering', handleRecovering);
		socket.on('game:recovered', handleRecovered);

		return () => {
			socket.off('game:recovering', handleRecovering);
			socket.off('game:recovered', handleRecovered);
		};
	}, [isLogged, playerId, username]);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
			cleanupActiveRequest();
		};
	}, [cleanupActiveRequest]);

	useEffect(() => {
		dispatch(activeUsersRequest());
	}, [dispatch]);

	const handlePlayNowPress = () => {
		if (!isLogged) return navigate('Login');

		console.log('Requesting active table...');

		if (isFindingTable) return;
		setIsFindingTable(true);
		setErrorTitle(t('T_ServerError_31'));
		setIsInsufficientChipsError(false);

		const socket = getSocket(String(playerId), String(username));
		const payload = { gameVersion: 4, attempt: 1 };

		cleanupActiveRequest();

		const clearFindingState = () => {
			setIsFindingTable(false);
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
		};

		const getTableFromPacket = (packet: any) => {
			const data = packet?.data || packet || {};
			const tableId = data?.tableId || data?.id || data?.table || data?.table_id;
			return { tableId, roomSlug: data?.roomSlug, legacyRoomId: data?.legacyRoomId };
		};

		const handleTableSuccess = (packet: any) => {
			const { tableId, roomSlug, legacyRoomId } = getTableFromPacket(packet);
			if (!tableId) return false;

			clearFindingState();
			cleanupActiveRequest();

			navigation.replace('Game', {
				tableId,
				roomSlug,
				legacyRoomId,
				isTournamentRoom: false,
			});
			return true;
		};

		const handleTableNotFound = (packet: any) => {
			if (handleTableSuccess(packet)) return;
			const shouldShowError =
				packet?.method === 'tableSeatNotFound' ||
				packet?.success === false ||
				(!packet?.success && packet?.data && Object.keys(packet.data).length === 0);

			if (!shouldShowError) return;

			clearFindingState();
			cleanupActiveRequest();
			// v1 lobby.component: ToastError on quickPlayNotEnoughChips (the error modal).
			playSound('ToastError');

			const rawError = packet?.error;
			const isTranslationKey = typeof rawError === 'string' && rawError.startsWith('T_');
			const isInsufficientBalance =
				packet?.errorCode === 14 || packet?.errorCode === 34 || rawError === 'T_ServerError_14';

			setIsInsufficientChipsError(isInsufficientBalance);
			setErrorTitle(
				isInsufficientBalance ? t('T_SITE_MODAL_NOT_ENOUGH_CHIPS_TITLE') : t('T_ServerError_31'),
			);
			setErrorMessage(isTranslationKey ? t(rawError) : rawError || t('T_SITE_MODAL_PROFILE_ERROR'));
			setShowErrorModal(true);
		};

		const handleQuickSeat = (packet: any) => {
			console.log('Quick seat response:', packet);
			if (handleTableSuccess(packet)) return;
			handleTableNotFound(packet);
		};

		const handleSeatNotFound = (packet: any) => {
			console.log('tableSeatNotFound event received:', packet);
			handleTableNotFound(packet);
		};

		const handleGetTable = (packet: any) => {
			console.log('getTable event received:', packet);
			if (handleTableSuccess(packet)) return;
			handleTableNotFound(packet);
		};

		socket.on('quickSeat', handleQuickSeat);
		socket.on('tableSeatNotFound', handleSeatNotFound);
		socket.on('getTable', handleGetTable);

		let connectHandler: (() => void) | null = null;

		requestCleanupRef.current = () => {
			socket.off('quickSeat', handleQuickSeat);
			socket.off('tableSeatNotFound', handleSeatNotFound);
			socket.off('getTable', handleGetTable);
			if (connectHandler) {
				socket.off('connect', connectHandler);
			}
		};

		if (timeoutRef.current) clearTimeout(timeoutRef.current);
		timeoutRef.current = setTimeout(() => {
			clearFindingState();
			cleanupActiveRequest();
			setIsInsufficientChipsError(false);
			setErrorTitle(t('T_ServerError_31'));
			setErrorMessage(t('T_SITE_MODAL_PROFILE_ERROR'));
			setShowErrorModal(true);
		}, 8000);

		const emitRequest = () => {
			console.log('📤 emit service:getActiveTable');
			socket.emit('service:getActiveTable', payload);
		};

		if (socket.connected) {
			emitRequest();
		} else {
			console.log('⏳ waiting for socket connect then emit...');
			connectHandler = emitRequest;
			socket.once('connect', connectHandler);
			socket.connect?.();
		}
	};

	const handleTryAgain = () => {
		setShowErrorModal(false);
		handlePlayNowPress();
	};

	const handleGoToLobby = () => {
		setShowErrorModal(false);
		navigate('Lobby');
	};

	const handleCancelError = () => {
		setShowErrorModal(false);
	};

	const handleBuyChips = () => {
		setShowErrorModal(false);
		navigate('BuyPackages', { activeTab: 'chips' });
	};

	return (
		<>
			{isDesktop ? (
				<HomeDesktopContent onPlayNowPress={handlePlayNowPress} />
			) : (
				<HomeRedesignContent onPlayNowPress={handlePlayNowPress} />
			)}

			<ErrorFindTableModal
				visible={showErrorModal}
				title={errorTitle}
				message={errorMessage}
				onTryAgain={isInsufficientChipsError ? handleBuyChips : handleTryAgain}
				tryAgainLabel={isInsufficientChipsError ? 'Buy Chips' : 'Try Again'}
				onGoToLobby={handleGoToLobby}
				onCancel={handleCancelError}
			/>
		</>
	);
};

export default MiddleSection;
